import { useState, type FormEvent } from "react";
import { Box, Button, Paper, Typography, Stack, Alert, CircularProgress, TextField, Link as MuiLink } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { forgotPassword } from "@/api/auth";
import { getErrorMessage } from "@/utils/getErrorMessage";
import athrivIcon from "@/assets/athriv-icon.png";

/** Reached from LoginPage's "Forgot password?" link - not part of the
 * authenticated router, since whoever lands here has no session yet. See
 * App.tsx for the routing. */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => forgotPassword(email),
    onError: (err: unknown) => setError(getErrorMessage(err))
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
      <Paper elevation={0} sx={{ p: 5, width: 380, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 48, height: 48, objectFit: "contain" }} />

          {mutation.isSuccess ? (
            <>
              <Typography variant="h5" sx={{ fontFamily: '"Manrope", sans-serif' }}>Check your email</Typography>
              <Typography variant="body2" color="text.secondary">
                If an account exists for {email}, we've sent a link to reset your password.
              </Typography>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="h5" sx={{ fontFamily: '"Manrope", sans-serif' }}>Reset your password</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Enter your email and we'll send you a link to set a new password.
                </Typography>
              </Box>
              <Stack component="form" spacing={2} sx={{ width: "100%" }} onSubmit={handleSubmit}>
                <TextField
                  label="Email" type="email" size="small" fullWidth required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
                <Button fullWidth variant="contained" type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Send reset link"}
                </Button>
              </Stack>
              {error && <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>{error}</Alert>}
            </>
          )}

          <MuiLink href="/login" variant="body2">Back to login</MuiLink>
        </Stack>
      </Paper>
    </Box>
  );
}
